import { useEffect, useRef } from 'react'
import * as echarts from 'echarts/core'
import type { EChartsOption } from 'echarts'
import { BarChart, HeatmapChart, LineChart, ScatterChart } from 'echarts/charts'
import {
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

echarts.use([
  CanvasRenderer,
  BarChart,
  GridComponent,
  HeatmapChart,
  LegendComponent,
  LineChart,
  MarkLineComponent,
  ScatterChart,
  TooltipComponent,
  VisualMapComponent,
])

export function EChart({ option, className = '', ariaLabel, onEvents }: { option: EChartsOption; className?: string; ariaLabel: string; onEvents?: Record<string, (params: unknown) => void> }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ref.current) return
    const chart = echarts.init(ref.current, undefined, { renderer: 'canvas' })
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    chart.setOption({ ...option, animation: !reducedMotion })
    Object.entries(onEvents ?? {}).forEach(([event, handler]) => chart.on(event, handler))
    const resize = new ResizeObserver(() => chart.resize())
    resize.observe(ref.current)
    return () => {
      resize.disconnect()
      chart.dispose()
    }
  }, [option, onEvents])

  return <div ref={ref} className={className} role="img" aria-label={ariaLabel} />
}
